/**
 * External dependencies
 */
import clsx from 'clsx';
// Import CompositeStore type, which is not exported from @wordpress/components.
// eslint-disable-next-line no-restricted-imports

/**
 * WordPress dependencies
 */
import { useInstanceId } from '@wordpress/compose';
import { __experimentalHStack as HStack, __experimentalVStack as VStack, Button, privateApis as componentsPrivateApis, Spinner, VisuallyHidden } from '@wordpress/components';
import { useCallback, useEffect, useMemo, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { moreVertical } from '@wordpress/icons';
import { useRegistry } from '@wordpress/data';

/**
 * Internal dependencies
 */
import { unlock } from './lock-unlock';
import { ActionsDropdownMenuGroup, ActionModal } from './item-actions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const {
  useCompositeStoreV2: useCompositeStore,
  CompositeV2: Composite,
  CompositeItemV2: CompositeItem,
  CompositeRowV2: CompositeRow,
  DropdownMenuV2: DropdownMenu
} = unlock(componentsPrivateApis);
function ListItem({
  actions,
  id,
  isSelected,
  item,
  mediaField,
  onSelect,
  primaryField,
  store,
  visibleFields
}) {
  const registry = useRegistry();
  const itemRef = useRef(null);
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  useEffect(() => {
    if (isSelected) {
      itemRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [isSelected]);
  const {
    primaryAction,
    eligibleActions
  } = useMemo(() => {
    // If an action is eligible for all items, doesn't need
    // to provide the `isEligible` function.
    const _eligibleActions = actions.filter(action => !action.isEligible || action.isEligible(item));
    const _primaryActions = _eligibleActions.filter(action => action.isPrimary && !!action.icon);
    return {
      primaryAction: _primaryActions?.[0],
      eligibleActions: _eligibleActions
    };
  }, [actions, item]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const primaryActionLabel = primaryAction && (typeof primaryAction.label === 'string' ? primaryAction.label : primaryAction.label([item]));
  return /*#__PURE__*/_jsx(CompositeRow, {
    ref: itemRef,
    render: /*#__PURE__*/_jsx("li", {}),
    role: "row",
    className: clsx({
      'is-selected': isSelected,
      'is-hovered': isHovered
    }),
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    children: /*#__PURE__*/_jsxs(HStack, {
      className: "dataviews-view-list__item-wrapper",
      alignment: "center",
      spacing: 0,
      children: [/*#__PURE__*/_jsx("div", {
        role: "gridcell",
        children: /*#__PURE__*/_jsx(CompositeItem, {
          store: store,
          render: /*#__PURE__*/_jsx("div", {}),
          role: "button",
          id: id,
          "aria-pressed": isSelected,
          "aria-labelledby": labelId,
          "aria-describedby": descriptionId,
          className: "dataviews-view-list__item",
          onClick: () => onSelect(item),
          children: /*#__PURE__*/_jsxs(HStack, {
            spacing: 3,
            justify: "start",
            alignment: "flex-start",
            children: [/*#__PURE__*/_jsx("div", {
              className: "dataviews-view-list__media-wrapper",
              children: mediaField?.render({
                item
              }) || /*#__PURE__*/_jsx("div", {
                className: "dataviews-view-list__media-placeholder"
              })
            }), /*#__PURE__*/_jsxs(VStack, {
              spacing: 0,
              children: [/*#__PURE__*/_jsx("span", {
                className: "dataviews-view-list__primary-field",
                id: labelId,
                children: primaryField?.render({
                  item
                })
              }), /*#__PURE__*/_jsx("div", {
                className: "dataviews-view-list__fields",
                id: descriptionId,
                children: visibleFields.map(field => /*#__PURE__*/_jsxs("div", {
                  className: "dataviews-view-list__field",
                  children: [/*#__PURE__*/_jsx(VisuallyHidden, {
                    as: "span",
                    className: "dataviews-view-list__field-label",
                    children: field.header
                  }), /*#__PURE__*/_jsx("span", {
                    className: "dataviews-view-list__field-value",
                    children: field.render({
                      item
                    })
                  })]
                }, field.id))
              })]
            })]
          })
        })
      }), eligibleActions?.length > 0 && /*#__PURE__*/_jsxs(HStack, {
        spacing: 1,
        justify: "flex-end",
        className: "dataviews-view-list__item-actions",
        style: {
          flexShrink: '0',
          width: 'auto'
        },
        children: [primaryAction && 'RenderModal' in primaryAction && /*#__PURE__*/_jsx("div", {
          role: "gridcell",
          children: /*#__PURE__*/_jsx(CompositeItem, {
            store: store,
            render: /*#__PURE__*/_jsx(Button, {
              label: primaryActionLabel,
              icon: primaryAction.icon,
              isDestructive: primaryAction.isDestructive,
              size: "compact",
              onClick: () => setIsModalOpen(true)
            }),
            children: isModalOpen && /*#__PURE__*/_jsx(ActionModal, {
              action: primaryAction,
              items: [item],
              closeModal: () => setIsModalOpen(false)
            })
          })
        }), primaryAction && !('RenderModal' in primaryAction) && /*#__PURE__*/_jsx("div", {
          role: "gridcell",
          children: /*#__PURE__*/_jsx(CompositeItem, {
            store: store,
            render: /*#__PURE__*/_jsx(Button, {
              label: primaryActionLabel,
              icon: primaryAction.icon,
              isDestructive: primaryAction.isDestructive,
              size: "compact",
              onClick: () => {
                primaryAction.callback([item], {
                  registry
                });
              }
            })
          })
        }, primaryAction.id), /*#__PURE__*/_jsx("div", {
          role: "gridcell",
          children: /*#__PURE__*/_jsx(DropdownMenu, {
            trigger: /*#__PURE__*/_jsx(CompositeItem, {
              store: store,
              render: /*#__PURE__*/_jsx(Button, {
                size: "compact",
                icon: moreVertical,
                label: __('Actions'),
                accessibleWhenDisabled: true,
                disabled: !actions.length,
                onKeyDown: event => {
                  if (event.key === 'ArrowDown') {
                    // Prevent the default behaviour (open dropdown menu) and go down.
                    event.preventDefault();
                    store.move(store.down());
                  }
                  if (event.key === 'ArrowUp') {
                    // Prevent the default behavior (open dropdown menu) and go up.
                    event.preventDefault();
                    store.move(store.up());
                  }
                }
              })
            }),
            placement: "bottom-end",
            children: /*#__PURE__*/_jsx(ActionsDropdownMenuGroup, {
              actions: eligibleActions,
              item: item
            })
          })
        })]
      })]
    })
  });
}
export default function ViewList(props) {
  const {
    actions,
    data,
    fields,
    getItemId,
    isLoading,
    onSelectionChange,
    selection,
    view
  } = props;
  const baseId = useInstanceId(ViewList, 'view-list');
  const selectedItem = data?.findLast(item => selection.includes(getItemId(item)));
  const mediaField = fields.find(field => field.id === view.layout?.mediaField);
  const primaryField = fields.find(field => field.id === view.layout?.primaryField);
  const viewFields = view.fields || fields.map(field => field.id);
  const visibleFields = fields.filter(field => viewFields.includes(field.id) && ![view.layout?.primaryField, view.layout?.mediaField].includes(field.id));
  const onSelect = item => onSelectionChange([getItemId(item)]);
  const getItemDomId = useCallback(item => item ? `${baseId}-${getItemId(item)}` : undefined, [baseId, getItemId]);
  const store = useCompositeStore({
    defaultActiveId: getItemDomId(selectedItem)
  });

  // Manage focused item, when the active one is removed from the list.
  const isActiveIdInList = store.useState(state => state.items.some(item => item.id === state.activeId));
  useEffect(() => {
    if (!isActiveIdInList) {
      // Prefer going down, except if there is no item below (last item), then go up (last item in list).
      if (store.down()) {
        store.move(store.down());
      } else if (store.up()) {
        store.move(store.up());
      }
    }
  }, [isActiveIdInList]);
  const hasData = data?.length;
  if (!hasData) {
    return /*#__PURE__*/_jsx("div", {
      className: clsx({
        'dataviews-loading': isLoading,
        'dataviews-no-results': !hasData && !isLoading
      }),
      children: !hasData && /*#__PURE__*/_jsx("p", {
        children: isLoading ? /*#__PURE__*/_jsx(Spinner, {}) : __('No results')
      })
    });
  }
  return /*#__PURE__*/_jsx(Composite, {
    id: baseId,
    render: /*#__PURE__*/_jsx("ul", {}),
    className: "dataviews-view-list",
    role: "grid",
    store: store,
    children: data.map(item => {
      const id = getItemDomId(item);
      return /*#__PURE__*/_jsx(ListItem, {
        id: id,
        actions: actions,
        item: item,
        isSelected: item === selectedItem,
        onSelect: onSelect,
        mediaField: mediaField,
        primaryField: primaryField,
        store: store,
        visibleFields: visibleFields
      }, id);
    })
  });
}
//# sourceMappingURL=view-list.js.map